import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Target, Shield, TrendingUp, Users, BookOpen, Building2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import FinancialCalculatorsCarousel from '../components/FinancialCalculatorsCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoaded, setTestimonialsLoaded] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials/page/home`, {
        timeout: 10000 // 10 second timeout
      });
      if (response.data && Array.isArray(response.data)) {
        setTestimonials(response.data);
      } else {
        setTestimonials([]);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      setTestimonials([]);
    } finally {
      setTestimonialsLoaded(true);
    }
  };

  const features = [
    {
      icon: <Target className="h-12 w-12 text-primary" />,
      title: "Goal-Based Investing",
      description: "Your money works towards your real-life goals, not just returns"
    },
    {
      icon: <Shield className="h-12 w-12 text-secondary" />,
      title: "Risk Management",
      description: "Protecting your investments through proper diversification and behavioral guidance"
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-primary" />,
      title: "Long-Term Vision",
      description: "Building wealth takes time - we're here with you in the journey"
    },
    {
      icon: <BookOpen className="h-12 w-12 text-secondary" />,
      title: "Continuous Learning",
      description: "Always updating knowledge through books, research, and market tracking"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" style={{backgroundColor: '#2C3540'}} data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(245,166,35,.03) 2px, rgba(245,166,35,.03) 4px), repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(245,166,35,.03) 2px, rgba(245,166,35,.03) 4px)',
            backgroundColor: '#2C3540'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-5xl md:text-7xl font-bold text-text-primary mb-6" data-testid="hero-title">
                Your Goals,<br />
                <span style={{color: "#F5A623"}}>My Mission</span>
              </h1>
              <p className="text-lg md:text-xl text-text-secondary mb-8 leading-relaxed" data-testid="hero-description">
                Transform your financial dreams into reality through goal-based mutual fund investing. 
                Professional guidance for retail investors and corporate clients.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary text-lg px-8 py-6" data-testid="hero-cta-primary">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/philosophy">
                  <Button className="btn-secondary text-lg px-8 py-6" data-testid="hero-cta-secondary">
                    Learn More
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 blur-3xl"></div>
                <img 
                  src="./logo.svg"
                  alt="Star Nivesh"
                  className="relative z-10 w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20" style={{backgroundColor: '#1F2937'}} data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Why Choose Star Nivesh?
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Your interests always come first. We combine ethical practice with proven investment strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-effect p-8 rounded-2xl hover:border-secondary/30 transition-all duration-300 hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">
                  {feature.title}
                </h3>
                <p className="text-text-secondary">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20" style={{backgroundColor: '#2C3540'}} data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Our Services
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Tailored investment solutions for different needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Retail Investors */}
            <Link to="/services/retail">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-effect rounded-2xl overflow-hidden transition-all group"
                data-testid="service-retail-card"
              >
                <div className="relative h-64">
                  <img 
                    src="https://images.unsplash.com/photo-1659352790654-058e9077a4f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYW1pbHklMjBoYXBweSUyMGZpbmFuY2lhbCUyMGZyZWVkb218ZW58MHx8fHwxNzY3NTA4Mjg4fDA&ixlib=rb-4.1.0&q=85"
                    alt="Retail Investors"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
                </div>
                <div className="p-8">
                  <Users className="h-12 w-12 text-primary mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors">
                    Retail Investors
                  </h3>
                  <p className="text-text-secondary mb-4">
                    Goal-based investment planning for individuals and families. Build wealth systematically through SIPs, risk management and strategic asset allocation.
                  </p>
                  <span className="text-primary font-semibold flex items-center">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </motion.div>
            </Link>

            {/* Corporate Clients */}
            <Link to="/services/corporate">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-effect rounded-2xl overflow-hidden transition-all group"
                data-testid="service-corporate-card"
              >
                <div className="relative h-64">
                  <img 
                    src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzY3NTA4Mjg5fDA&ixlib=rb-4.1.0&q=85"
                    alt="Corporate Clients"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
                </div>
                <div className="p-8">
                  <Building2 className="h-12 w-12 text-secondary mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-text-primary mb-3 group-hover:text-secondary transition-colors">
                    Corporate Clients
                  </h3>
                  <p className="text-text-secondary mb-4">
                    Optimize idle cash in current accounts through strategic debt fund investments. Better returns than Bank's Fixed Deposit & zero-interest bank accounts.
                  </p>
                  <span className="text-secondary font-semibold flex items-center">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* Financial Calculators Section */}
      <FinancialCalculatorsCarousel backgroundColor="#1F2937" />

      {/* Client Testimonials Section */}
      <TestimonialsCarousel testimonials={testimonials} isLoaded={testimonialsLoaded} backgroundColor="#2C3540" />

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#1F2937'}} data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Ready to Start Your Investment Journey?
          </h2>
          <p className="text-text-secondary text-lg mb-8">
            Let's talk about where you want to go and how we can get there together.
          </p>
          <Link to="/contact">
            <Button className="btn-primary text-lg px-10 py-6" data-testid="cta-button">
              Schedule a Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
